import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { Ref } from 'vue'

export function useAutoCollapse(
    outer: Ref<HTMLElement | null>,
    inner: Ref<HTMLElement | null>,
    state: Ref<boolean>,
) {
    let lastKnownHeight = 0
    const isContentCollapsed = ref(false)

    function applyOpen(el: HTMLElement) {
        el.style.height = ''
        el.style.maxHeight = lastKnownHeight + 'px'
    }

    function applyClose(el: HTMLElement) {
        el.style.maxHeight = lastKnownHeight + 'px'
        requestAnimationFrame(() => {
            el.style.maxHeight = '0px'
        })
    }

    function onTransitionEnd(e: TransitionEvent) {
        if (e.propertyName !== 'max-height') return
        const el = outer.value
        if (!el) return
        if (state.value && !isContentCollapsed.value) {
            el.style.maxHeight = 'none'
        } else {
            el.style.height = ''
        }
    }

    function onResize(entries: ResizeObserverEntry[]) {
        const el = outer.value
        if (!el) return
        const contentHeight = entries[0]?.contentRect.height ?? 0
        const fullHeight = inner.value?.offsetHeight ?? 0
        if (contentHeight > 0) {
            const wasContentCollapsed = isContentCollapsed.value
            lastKnownHeight = fullHeight
            isContentCollapsed.value = false
            if (state.value) {
                if (wasContentCollapsed) {
                    applyOpen(el)
                } else if (el.style.maxHeight !== 'none') {
                    el.style.maxHeight = lastKnownHeight + 'px'
                }
            }
        } else if (state.value) {
            el.style.height = lastKnownHeight + 'px'
            isContentCollapsed.value = true
            applyClose(el)
        }
    }

    watch(
        () => state.value,
        (currentState) => {
            const el = outer.value
            const innerEl = inner.value
            if (!el) return
            isContentCollapsed.value = false
            if (currentState) {
                if (innerEl) {
                    const h = innerEl.scrollHeight
                    if (h > 0) lastKnownHeight = h
                }
                applyOpen(el)
            } else {
                applyClose(el)
            }
        },
        { flush: 'post' },
    )

    let observer: ResizeObserver | undefined

    onMounted(() => {
        const innerEl = inner.value
        const outerEl = outer.value
        if (!innerEl || !outerEl) return
        const style = window.getComputedStyle(innerEl)
        const paddingVertical = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
        const hasContent = innerEl.scrollHeight > paddingVertical
        lastKnownHeight = hasContent ? innerEl.offsetHeight : 0
        observer = new ResizeObserver(onResize)
        observer.observe(innerEl)
        outerEl.style.maxHeight = (state.value && hasContent) ? lastKnownHeight + 'px' : '0px'
    })

    onBeforeUnmount(() => {
        observer?.disconnect()
    })

    return { onTransitionEnd, isContentCollapsed }
}
